import type { Event } from '@google/adk';
import type { Content, Part } from '@google/genai';
import { getStrapiClient } from '../../../lib/strapi';
import type { AgentStep, A2uiMessage, Thread } from '../../../types';
import { generateToolConfirmationA2UI } from '../utils/messageParser';

export type StreamAction =
  | { type: 'text_chunk'; text: string; event: Event }
  | { type: 'status'; status: string }
  | { type: 'step_update'; step: AgentStep }
  | { type: 'raw_a2ui'; message: A2uiMessage }
  | { type: 'done' }
  | { type: 'error'; error: string };

export const fetchThreadHistory = async (sessionId: string): Promise<Thread | null> => {
  const strapi = getStrapiClient();
  try {
    const response = await strapi.collection('threads').find({
      filters: { externalId: sessionId }
    });

    const threads = response.data;
    if (threads && threads.length > 0) {
      return threads[0];
    }
    return null;
  } catch (error) {
    console.error("Error fetching thread history:", error);
    return null;
  }
};

export const queryAgentStreamEvents = async (
  parts: Part[],
  emailContext: string | undefined,
  sessionId: string | undefined,
  onAction: (action: StreamAction) => void
) => {
  const token = localStorage.getItem("strapi_jwt");
  const organizationId = localStorage.getItem("organization_id");
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (organizationId) {
    headers['organization-document-id'] = organizationId;
  }

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const response = await fetch(
    process.env.NODE_ENV === 'production'
      ? '/api/chat'
      : 'http://localhost:3001/api/chat',
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ parts, emailContext, sessionId, timeZone })
    });
  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new Event('unauthorized'));
    }
    throw new Error(`Server error: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error("No reader available");

  const decoder = new TextDecoder();
  let buffer = "";
  const streamingInvocations = new Set<string>();

  const processEventData = (dataStr: string) => {
    if (dataStr === '[DONE]') {
      onAction({ type: 'done' });
      return true; // Stop processing
    }

    try {
      const rawObj = JSON.parse(dataStr);
      const event = (rawObj.event ? rawObj.event : rawObj) as Event;

      if (event.content?.role === "developer") {
        return false;
      }

      if (event.errorCode) {
        const errorMsg = event.errorMessage || event.errorCode;
        onAction({ type: 'error', error: errorMsg });
        onAction({ type: 'step_update', step: { id: `error-${event.id}`, text: `Error: ${errorMsg}`, done: true } });
        return false; // Error event usually indicates end, but we let stream finish or handle it in UI
      }

      if (event.actions?.transferToAgent) {
        onAction({ type: 'status', status: `Transferring to ${event.actions.transferToAgent.replace(/_/g, ' ')}...` });
      } else if (event.author && event.author !== 'root_agent' && event.author !== 'orchestrator') {
        onAction({ type: 'status', status: `Agent: ${event.author.replace(/_/g, ' ')}` });
      } else if (typeof rawObj.status === 'string') {
        onAction({ type: 'status', status: rawObj.status });
      }

      const invId = event.invocationId || event.id;

      if (event.content?.parts && Array.isArray(event.content.parts)) {
        const isPartial = event.partial === true;
        if (isPartial && invId) {
          streamingInvocations.add(invId);
        }

        // If this chunk is NOT partial, but we already streamed its partials, skip text appending
        const skipText = !isPartial && invId && streamingInvocations.has(invId);

        for (const p of event.content.parts) {
          if (p.text && !skipText) {
            onAction({ type: 'text_chunk', text: p.text, event });
          } else if (p.functionCall) {
            const name = p.functionCall.args?.agentName || p.functionCall.name;
            const callId = p.functionCall.id || `call-${Date.now()}`;
            const agentName = String(name).replace(/_/g, ' ');
            onAction({ type: 'status', status: `Running action: ${agentName}...` });

            if (p.functionCall.name === 'transfer_to_agent') {
              const target = p.functionCall.args?.agentName || 'agent';
              onAction({ type: 'step_update', step: { id: callId, text: `Transferring to ${String(target).replace(/_/g, ' ')}...`, done: false } });
            } else {
              onAction({ type: 'step_update', step: { id: callId, text: `Running action: ${agentName}...`, done: false } });
            }
          } else if (p.functionResponse) {
            const callId = p.functionResponse.id || `resp-${Date.now()}`;
            const name = String(p.functionResponse.name).replace(/_/g, ' ');
            onAction({ type: 'status', status: `Action completed: ${name}` });

            if (p.functionResponse.name === 'transfer_to_agent') {
              // We indicate transfer is complete (partially), waiting for new agent response
              // We will handle the "done=false" spinning in the UI reducer until next text arrives
              const target = event.actions?.transferToAgent ? String(event.actions.transferToAgent).replace(/_/g, ' ') : 'new agent';
              onAction({ type: 'step_update', step: { id: p.functionResponse.id!, text: `Transferred to ${target}...`, done: false } });
            } else {
              onAction({ type: 'step_update', step: { id: callId, text: `Action completed: ${name}`, done: true } });
            }
          }
        }
      }

      if (event.actions?.requestedToolConfirmations && Object.keys(event.actions.requestedToolConfirmations).length > 0) {
        onAction({ type: 'status', status: "Waiting for tool confirmation..." });
        const a2uiMsgs = generateToolConfirmationA2UI(event);
        for (const msg of a2uiMsgs) {
          onAction({ type: 'raw_a2ui', message: msg });
        }
      }

      // Check for legacy unstructured raw A2UI commands at the top level
      if (rawObj.type && ['createSurface', 'updateComponents', 'updateDataModel', 'deleteSurface'].includes(rawObj.type)) {
        onAction({ type: 'raw_a2ui', message: rawObj });
      }

      if (event.finishReason && !event.errorCode) {
        onAction({ type: 'status', status: "" });
      }

    } catch (e) {
      console.warn("Failed to parse stream chunk as JSON:", dataStr);
    }

    return false;
  };

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        if (buffer.trim()) {
          let dataStr = buffer.trim();
          if (dataStr.startsWith('data:')) {
            dataStr = dataStr.slice(5).trim();
          }
          processEventData(dataStr);
        }
        onAction({ type: 'done' });
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      let newlineIndex;
      while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);

        if (!line) continue;

        let dataStr = line;
        if (line.startsWith('data:')) {
          dataStr = line.slice(5).trim();
        }

        const isDone = processEventData(dataStr);
        if (isDone) return;
      }
    }
  } finally {
    reader.releaseLock();
  }
};

export const cancelAgentRun = async (sessionId: string) => {
  const token = localStorage.getItem("strapi_jwt");
  const organizationId = localStorage.getItem("organization_id");
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (organizationId) {
    headers['organization-document-id'] = organizationId;
  }

  const response = await fetch(
    process.env.NODE_ENV === 'production'
      ? '/api/cancelAdkRun'
      : 'http://localhost:3001/api/cancelAdkRun',
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ sessionId })
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to cancel run: ${response.status}`);
  }
};
